import {
	getGetEventTypeQueryKey,
	getListEventTypesQueryKey,
	useGetEventType,
	useUpdateEventType,
} from '@cocoonspace/sdk-js/v2/domains/schedulers/schedulers'
import { useLingui } from '@lingui/react/macro'
import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { toast } from 'sonner'

import { ErrorMsg } from '~/components/error-msg'
import { PageLoader } from '~/components/page-loader'
import {
	type EditEventTypeFormData,
	EventTypeForm,
	type LocationSchema,
} from './event-type-form'

export const EditEventType = ({
	id,
	onSuccess,
}: {
	id: string
	onSuccess?: VoidFunction
}) => {
	const { t } = useLingui()
	const eventTypeQuery = useGetEventType(id)
	const updateEventTypeMutation = useUpdateEventType()
	const queryClient = useQueryClient()

	const handleSubmit = async (data: EditEventTypeFormData) => {
		return updateEventTypeMutation
			.mutateAsync({ id, data })
			.then(() => {
				toast.success(t`Type de rendez-vous mis à jour`)

				queryClient.invalidateQueries({
					queryKey: getGetEventTypeQueryKey(id),
				})

				queryClient.invalidateQueries({
					queryKey: getListEventTypesQueryKey(),
				})

				onSuccess?.()
			})
			.catch((e) => {
				if (isAxiosError(e) && e.response?.status === 409) {
					toast.error(t`Slug déjà utilisé`)
				}
			})
	}

	if (eventTypeQuery.isPending) return <PageLoader />

	if (eventTypeQuery.isError) {
		return (
			<ErrorMsg
				message={t`Une erreur est survenue lors de la récupération du type de rendez-vous`}
			/>
		)
	}

	return (
		<EventTypeForm
			defaultValues={{
				...eventTypeQuery.data,
				description: eventTypeQuery.data.description ?? undefined,
				location: (eventTypeQuery.data.location as LocationSchema) ?? undefined,
				meetingBuffer: eventTypeQuery.data.meetingBuffer ?? undefined,
			}}
			isLoading={updateEventTypeMutation.isPending}
			onSubmit={handleSubmit}
		/>
	)
}
